import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TranslocoService} from "@ngneat/transloco";
import {ActivatedRoute, Router} from "@angular/router";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {AuthenticationService, DiscountsService, OrganiserService, TOAST_STATE, ToastService} from "@services";
import {AbstractComponent, ConfirmationModalComponent} from "@components";
import {Discount, DiscountCoversType, Lookups, Organiser} from "@model";
import {DISCOUNT_DETAILS} from "src/app/app-routing.module";

@Component({
  selector: 'app-list-of-discounts',
  templateUrl: './list-of-discounts.component.html',
  styleUrls: ['./list-of-discounts.component.scss']
})
export class ListOfDiscountsComponent extends AbstractComponent implements OnInit {

  @Input() listOfDiscounts: Discount[] | undefined;
  @Input() languages: Lookups[] = [];
  @Input() isAdminRole: boolean = false;
  @Output() discountsChanged: EventEmitter<Discount[]> = new EventEmitter<Discount[]>();

  currentOrganiser: Organiser | undefined;

  constructor(public translocoService: TranslocoService, private discountsService: DiscountsService,
              private modalService: NgbModal, private organiserService: OrganiserService, private router: Router,
              private toastService: ToastService, public authenticationService: AuthenticationService,
              private route: ActivatedRoute) {
    super();
  }

  ngOnInit(): void {
    this.subscribeToChangeOrganiser();
  }

  private subscribeToChangeOrganiser() {
    this.subscriptions.push(this.organiserService.currentOrganiser.subscribe((value: Organiser | undefined) => {
      if (value) {
        this.currentOrganiser = value;
      }
    }));
  }

  deleteDiscount(discount: Discount) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("administrator.discounts.management.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("administrator.discounts.management.delete.dialog.text");

    modalRef.result.then((_) => {
      this.onDeleteDiscount(discount);
    }, (_) => {
      /// ignore
    });
  }

  onDeleteDiscount(discount: Discount): void {
    this.discountsService.deleteDiscount(discount, this.currentOrganiser?.id).subscribe((result: boolean) => {
      if (result) {
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("administrator.discounts.management.delete.success"))
        this.discountsService.findDiscounts(this.currentOrganiser?.id).subscribe((discounts: Discount[]) => {
          this.listOfDiscounts = discounts;
          if (this.isAdminRole) {
            this.discountsChanged.emit(this.listOfDiscounts);
          }
        });
      } else {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.server.error"))
      }
    })
  }

  activateDiscount(discount: Discount) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("administrator.discounts.management.activate.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("administrator.discounts.management.activate.dialog.text");

    modalRef.result.then((_) => {
      this.onActivateDiscount(discount);
    }, (_) => {
      /// ignore
    });
  }

  onActivateDiscount(discount: Discount): void {
    this.discountsService.activateDiscount(discount).subscribe((result: boolean) => {
      if (result) {
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("administrator.discounts.management.onactivate.success"))
        this.discountsService.findDiscounts(this.currentOrganiser?.id).subscribe((discounts: Discount[]) => {
          this.listOfDiscounts = discounts;
          if (this.isAdminRole) {
            this.discountsChanged.emit(this.listOfDiscounts);
          }
        })
      } else {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.server.error"))
      }
    })
  }

  deactivateDiscount(discount: Discount) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("administrator.discounts.management.deactivate.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("administrator.discounts.management.deactivate.dialog.text");

    modalRef.result.then((_) => {
      this.onDeactivateDiscount(discount);
    }, (_) => {
      /// ignore
    });
  }

  onDeactivateDiscount(discount: Discount): void {
    this.discountsService.deactivateDiscount(discount).subscribe((result: boolean) => {
      if (result) {
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate("administrator.discounts.management.ondeactivate.success"))
        this.discountsService.findDiscounts(this.currentOrganiser?.id).subscribe((discounts: Discount[]) => {
          this.listOfDiscounts = discounts;
          if (this.isAdminRole) {
            this.discountsChanged.emit(this.listOfDiscounts);
          }
        })
      } else {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.server.error"))
      }
    })
  }

  createNewCode() {
    if (this.currentOrganiser) {
      this.router.navigate([DISCOUNT_DETAILS], {
        queryParams: {organiserId: this.currentOrganiser.id}, relativeTo: this.route,
        queryParamsHandling: 'merge'
      });
    } else {
      this.router.navigate([DISCOUNT_DETAILS], {relativeTo: this.route, queryParamsHandling: 'merge'});
    }
  }

  goToDiscountDetails(discount: Discount): void {
    this.router.navigate([DISCOUNT_DETAILS], {
      queryParams: {discountId: discount.id, organiserId: this.currentOrganiser?.id}, relativeTo: this.route,
      queryParamsHandling: 'merge'
    });
  }

  getNumberOfRacesOnDiscount(discount: Discount): number {
    var numberOfRaces: number = 0;
    if ((discount.events?.length ?? 0)>0) {
      for (let event of discount.events!) {
        if ((event.racesList?.length ?? 0)>0) {
          for (let race of event.racesList!) {
            if (race.discountApplied) {
              numberOfRaces++;
            }
          }
        }
      }
    }
    return numberOfRaces;
  }

  protected readonly DiscountCoversType = DiscountCoversType;
}
